/**
 * Navigation au clavier d'une arborescence (motif « Tree View » de l'APG WAI-ARIA 1.2) — module sans React, testé.
 *
 * ↓ / ↑ : nœud visible suivant / précédent ; Début / Fin : premier / dernier ; → : ouvre, puis descend au premier
 * enfant ; ← : ferme, sinon remonte au parent ; Entrée / Espace : active ; une lettre : prochain nœud qui commence
 * par cette lettre ; * : ouvre tous les frères du nœud.
 */
export interface NoeudNavigable {
  id: string;
  libelle: string;
  enfants: readonly NoeudNavigable[];
}

export interface NoeudVisible<N extends NoeudNavigable = NoeudNavigable> {
  noeud: N;
  niveau: number;
  parent: string | null;
  position: number;
  taille: number;
}

/** Les nœuds affichés, dans l'ordre de lecture, compte tenu des branches ouvertes. */
export function aplatir<N extends NoeudNavigable>(
  racines: readonly N[],
  ouverts: ReadonlySet<string>,
): NoeudVisible<N>[] {
  const sortie: NoeudVisible<N>[] = [];
  const parcourir = (liste: readonly N[], niveau: number, parent: string | null) =>
    liste.forEach((noeud, i) => {
      sortie.push({ noeud, niveau, parent, position: i + 1, taille: liste.length });
      if (noeud.enfants.length > 0 && ouverts.has(noeud.id))
        parcourir(noeud.enfants as readonly N[], niveau + 1, noeud.id);
    });
  parcourir(racines, 1, null);
  return sortie;
}

export type EffetClavier = { focus?: string; ouvrir?: string[]; fermer?: string; activer?: string };

const normaliser = (t: string) =>
  t
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .toLocaleLowerCase("fr-FR");

export function effetTouche(
  visibles: readonly NoeudVisible[],
  courant: string,
  touche: string,
): EffetClavier | null {
  const i = visibles.findIndex((v) => v.noeud.id === courant);
  if (i < 0) return visibles[0] ? { focus: visibles[0].noeud.id } : null;
  const v = visibles[i]!;
  const aDesEnfants = v.noeud.enfants.length > 0;
  const ouvert = aDesEnfants && visibles[i + 1]?.parent === v.noeud.id;
  switch (touche) {
    case "ArrowDown":
      return visibles[i + 1] ? { focus: visibles[i + 1]!.noeud.id } : null;
    case "ArrowUp":
      return i > 0 ? { focus: visibles[i - 1]!.noeud.id } : null;
    case "Home":
      return { focus: visibles[0]!.noeud.id };
    case "End":
      return { focus: visibles.at(-1)!.noeud.id };
    case "ArrowRight":
      if (!aDesEnfants) return null;
      return ouvert ? { focus: v.noeud.enfants[0]!.id } : { ouvrir: [v.noeud.id] };
    case "ArrowLeft":
      if (ouvert) return { fermer: v.noeud.id };
      return v.parent ? { focus: v.parent } : null;
    case "Enter":
    case " ":
      return { activer: v.noeud.id };
    case "*": {
      const freres = visibles
        .filter((x) => x.parent === v.parent && x.noeud.enfants.length > 0)
        .map((x) => x.noeud.id);
      return freres.length ? { ouvrir: freres } : null;
    }
    default: {
      if (touche.length !== 1 || !/\p{L}|\p{N}/u.test(touche)) return null;
      const lettre = normaliser(touche);
      const ordre = [...visibles.slice(i + 1), ...visibles.slice(0, i + 1)];
      const cible = ordre.find((x) => normaliser(x.noeud.libelle).startsWith(lettre));
      return cible && cible.noeud.id !== courant ? { focus: cible.noeud.id } : null;
    }
  }
}

/** Identifiants des ancêtres d'un nœud (pour ouvrir le chemin jusqu'à lui). */
export function ancetres(racines: readonly NoeudNavigable[], id: string): string[] {
  const chemin: string[] = [];
  const chercher = (liste: readonly NoeudNavigable[]): boolean => {
    for (const n of liste) {
      if (n.id === id) return true;
      chemin.push(n.id);
      if (chercher(n.enfants)) return true;
      chemin.pop();
    }
    return false;
  };
  return chercher(racines) ? chemin : [];
}
